import { numberWithCommas } from "js/utils";

export const normalizeFloat = (decimalNumbers = 2) => (value = "") => {
  if (!value && value !== 0) return "";

  let normalized = value.toString().trim();
  if (!normalized) return "";

  // Para números enteros (decimalNumbers = 0), todos los puntos son separadores de miles
  if (decimalNumbers === 0) {
    // Eliminar todos los puntos (son separadores de miles) y comas (no válidas para enteros)
    normalized = normalized.replace(/[.,]/g, "");
    // Eliminar todo lo que no sea dígito
    normalized = normalized.replace(/[^0-9]/g, "");
    return normalized;
  }

  // Para números decimales, lógica existente
  // Detectar si hay coma (separador decimal preferido)
  const hasComma = normalized.includes(",");
  const hasDot = normalized.includes(".");

  if (hasComma) {
    // Coma como separador decimal: eliminar todos los puntos (miles)
    normalized = normalized.replace(/\./g, "");
    normalized = normalized.replace(",", ".");
  } else if (hasDot) {
    // Punto: si hay múltiples, el último es decimal, los demás son miles
    const lastDotIndex = normalized.lastIndexOf(".");
    if (lastDotIndex !== normalized.indexOf(".")) {
      // Múltiples puntos: eliminar todos y dejar solo el último
      const beforeLastDot = normalized
        .slice(0, lastDotIndex)
        .replace(/\./g, "");
      const afterLastDot = normalized.slice(lastDotIndex + 1);
      normalized = beforeLastDot + "." + afterLastDot;
    }
  }

  // Eliminar todo lo que no sea dígito o punto decimal
  normalized = normalized.replace(/[^0-9.]/g, "");

  // No permitir separador sin dígitos
  const digitsCount = (normalized.match(/[0-9]/g) || []).length;
  if (digitsCount === 0) {
    return normalized.includes(".") ? "" : normalized;
  }

  const length = normalized.length - 1;

  // Si termina en punto, devolver con coma para continuar escribiendo
  if (normalized[length] === ".") {
    return normalized.slice(0, -1) + ",";
  }

  // NO aplicar toFixed mientras el usuario está escribiendo
  // Solo devolver el valor normalizado tal cual
  return normalized;
};

export const formatFloat = (decimalNumbers = 2) => (value = "", name) => {
  if (value === "-") return value;
  if (!value && value !== 0) return undefined;

  let val = value.toString().trim();
  if (!val || !val.length) return undefined;

  // Para números enteros (decimalNumbers = 0), todos los puntos son separadores de miles
  if (decimalNumbers === 0) {
    // Eliminar puntos y comas, solo quedarse con dígitos
    const cleaned = val.replace(/[.,]/g, "").replace(/[^0-9]/g, "");
    if (!cleaned) return undefined;
    const num = parseInt(cleaned, 10);
    if (isNaN(num)) return "";
    // Formatear con puntos de miles pero sin decimales
    return numberWithCommas(num, 0);
  }

  // Para números decimales, lógica existente
  const endsWithComma = val[val.length - 1] === ",";
  const raw = endsWithComma ? val.slice(0, -1) : val;

  // Detectar si tiene formato con puntos (miles) y coma (decimal)
  const hasComma = raw.includes(",");
  const hasDot = raw.includes(".");

  let cleaned;
  if (hasComma) {
    // Tiene coma: eliminar puntos (miles) y convertir coma a punto para parseFloat
    cleaned = raw.replace(/\./g, "").replace(",", ".");
  } else if (hasDot) {
    // Solo tiene punto: verificar si es decimal o miles
    const lastDotIndex = raw.lastIndexOf(".");
    const beforeLastDot = raw.slice(0, lastDotIndex);
    const afterLastDot = raw.slice(lastDotIndex + 1);

    // Si después del último punto hay 1-2 dígitos, es separador decimal
    // Si hay más de 2 dígitos, probablemente es miles (formato incorrecto)
    if (afterLastDot.length <= 2 && beforeLastDot.length > 0) {
      // Es separador decimal: eliminar solo los puntos de miles (antes del último)
      // Mantener el último punto como separador decimal
      const beforeLastDotCleaned = beforeLastDot.replace(/\./g, "");
      cleaned = beforeLastDotCleaned + "." + afterLastDot;
    } else {
      // Probablemente es miles o formato incorrecto, tratar como entero
      cleaned = raw.replace(/\./g, "");
    }
  } else {
    // No tiene separadores, es un entero
    cleaned = raw;
  }

  const num = parseFloat(cleaned);

  if (isNaN(num)) return "";

  return endsWithComma
    ? numberWithCommas(num, decimalNumbers) + ","
    : numberWithCommas(num, decimalNumbers);
};

export const intParser = (value = "") => {
  // Manejar valores null/undefined/vacíos
  if (!value && value !== 0) return null;

  // Eliminar puntos (miles) y comas, luego parsear
  const val = value
    .toString()
    .replace(/\./g, "")
    .replace(/,/g, "");
  return !isNaN(parseInt(val, 10)) ? parseInt(val, 10) : null;
};

export const floatParser = (value = "") => {
  if (!value && value !== 0) return null;

  // Convertir a string y limpiar espacios
  const str = value.toString().trim();
  if (!str) return null;

  // Detectar si hay coma (separador decimal en español)
  const hasComma = str.includes(",");

  let cleaned;
  if (hasComma) {
    // Formato español: eliminar puntos (miles) y convertir coma a punto (decimal)
    cleaned = str.replace(/\./g, "").replace(",", ".");
  } else {
    // Si solo hay puntos, el último podría ser decimal o todos son miles
    // Para ser seguros, eliminar todos los puntos excepto el último si hay dígitos después
    const lastDotIndex = str.lastIndexOf(".");
    if (lastDotIndex >= 0) {
      const afterLastDot = str.slice(lastDotIndex + 1);
      // Si hay 1-2 dígitos después del último punto, es decimal
      if (afterLastDot.length > 0 && afterLastDot.length <= 2) {
        const beforeLastDot = str.slice(0, lastDotIndex).replace(/\./g, "");
        cleaned = beforeLastDot + "." + afterLastDot;
      } else {
        // Todos son separadores de miles
        cleaned = str.replace(/\./g, "");
      }
    } else {
      cleaned = str;
    }
  }

  const num = parseFloat(cleaned);
  return !isNaN(num) ? num : null;
};

export const normalizeFloat_V2 = (decimalNumbers = 2) => (value = "") => {
  if (!value && value !== 0) return "";

  let normalized = value.toString().trim();
  if (!normalized) return "";

  // Si no hay coma pero hay puntos, verificar si son miles
  if (!normalized.includes(",")) {
    const lastDotIndex = normalized.lastIndexOf(".");
    if (lastDotIndex >= 0) {
      const afterLastDot = normalized.slice(lastDotIndex + 1);
      // Heurística de floatParser: si hay más de 2 dígitos tras el punto, son miles
      if (afterLastDot.length > 2) {
        normalized = normalized.replace(/\./g, "");
      }
    }
  } else {
    // Si hay coma, todos los puntos son miles
    normalized = normalized.replace(/\./g, "");
  }

  // A partir de aquí, seguir lógica estándar de normalizeFloat
  const hasComma = normalized.includes(",");
  const hasDot = normalized.includes(".");

  if (hasComma) {
    normalized = normalized.replace(/\./g, "");
    normalized = normalized.replace(",", ".");
  } else if (hasDot) {
    const lastDotIndex = normalized.lastIndexOf(".");
    if (lastDotIndex !== normalized.indexOf(".")) {
      const beforeLastDot = normalized
        .slice(0, lastDotIndex)
        .replace(/\./g, "");
      const afterLastDot = normalized.slice(lastDotIndex + 1);
      normalized = beforeLastDot + "." + afterLastDot;
    }
  }

  normalized = normalized.replace(/[^0-9.]/g, "");
  const digitsCount = (normalized.match(/[0-9]/g) || []).length;
  if (digitsCount === 0) {
    return normalized.includes(".") ? "" : normalized;
  }

  const length = normalized.length - 1;
  if (normalized[length] === ".") {
    return normalized.slice(0, -1) + ",";
  }

  return normalized;
};

export const formatFloat_V2 = (decimalNumbers = 2) => (value = "", name) => {
  if (value === "-") return value;
  if (!value && value !== 0) return undefined;

  let val = value.toString().trim();
  if (!val || !val.length) return undefined;

  // Usar una lógica similar a floatParser para limpiar antes de reformatear
  const hasComma = val.includes(",");
  let cleaned;

  if (hasComma) {
    cleaned = val.replace(/\./g, "").replace(",", ".");
  } else {
    const lastDotIndex = val.lastIndexOf(".");
    if (lastDotIndex >= 0) {
      const afterLastDot = val.slice(lastDotIndex + 1);
      if (afterLastDot.length > 0 && afterLastDot.length <= 2) {
        const beforeLastDot = val.slice(0, lastDotIndex).replace(/\./g, "");
        cleaned = beforeLastDot + "." + afterLastDot;
      } else {
        cleaned = val.replace(/\./g, "");
      }
    } else {
      cleaned = val;
    }
  }

  const num = parseFloat(cleaned);
  if (isNaN(num)) return "";

  const endsWithComma = val[val.length - 1] === ",";

  return endsWithComma
    ? numberWithCommas(num, decimalNumbers) + ","
    : numberWithCommas(num, decimalNumbers);
};
